import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Alert,
  MenuItem,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

const LoanApplication = () => {
  const { user } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [loanTemplates, setLoanTemplates] = useState([]);
  const [customerLoans, setCustomerLoans] = useState([]);
  const [loadingLoans, setLoadingLoans] = useState(false);
  const [formData, setFormData] = useState({
    customerId: '',
    loanTemplateId: '',
    notes: '',
  });
  const [message, setMessage] = useState('');
  const [loadingCustomers, setLoadingCustomers] = useState(false);

  useEffect(() => {
    fetchLoanTemplates();
    fetchCustomers();
  }, []);

  useEffect(() => {
    if (formData.customerId) {
      fetchCustomerLoans();
    } else {
      setCustomerLoans([]);
    }
  }, [formData.customerId]);

  const fetchCustomers = async () => {
    try {
      setLoadingCustomers(true);
      const params = new URLSearchParams();
      params.append('type', 'NEW');
      
      const response = await api.get(`/officer/customers?${params.toString()}`);
      const customersWithoutLoans = response.data || [];
      
      // Sort by createdAt DESC to get most recently registered first
      customersWithoutLoans.sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
        const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
        return dateB - dateA; // Most recent first
      });
      
      setCustomers(customersWithoutLoans);
      setFilteredCustomers(customersWithoutLoans);
    } catch (error) {
      console.error('Error fetching customers:', error);
      setCustomers([]);
      setFilteredCustomers([]);
      setMessage('Error fetching customers: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoadingCustomers(false);
    }
  };

  const fetchCustomerLoans = async () => {
    if (!formData.customerId) return;
    setLoadingLoans(true);
    try {
      const response = await api.get(`/officer/customers/${formData.customerId}/loans`);
      
      // Handle different response structures
      let loansArray = [];
      if (response.data) {
        if (Array.isArray(response.data)) {
          loansArray = response.data;
        } else if (response.data.loans && Array.isArray(response.data.loans)) {
          loansArray = response.data.loans;
        } else if (response.data.loans && !Array.isArray(response.data.loans)) {
          // Single loan object
          loansArray = [response.data.loans];
        }
      }
      
      console.log('Fetched customer loans:', loansArray.length, loansArray);
      setCustomerLoans(loansArray);
      
      // Check if customer has arrears and show immediate alert
      const hasArrears = loansArray.some(loan => loan.closedByAdmin && loan.arrearsAmount && loan.arrearsAmount > 0);
      if (hasArrears) {
        const totalArrears = loansArray
          .filter(loan => loan.closedByAdmin && loan.arrearsAmount && loan.arrearsAmount > 0)
          .reduce((sum, loan) => sum + (parseFloat(loan.arrearsAmount) || 0), 0);
        setMessage(`⚠️ WARNING: This customer has outstanding arrears of Rs ${totalArrears.toFixed(2)} from previous admin-closed loans. Please review before proceeding.`);
      }
    } catch (error) {
      console.error('Error fetching customer loans:', error);
      console.error('Error response:', error.response?.data);
      setCustomerLoans([]);
      // Don't show error message for 404, just log it
      if (error.response?.status !== 404) {
        setMessage('Error fetching loan history: ' + (error.response?.data?.error || error.message));
      }
    } finally {
      setLoadingLoans(false);
    }
  };

  const fetchLoanTemplates = async () => {
    try {
      const response = await api.get('/officer/loan-templates');
      setLoanTemplates(response.data);
    } catch (error) {
      console.error('Error fetching loan templates:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const selectedTemplate = loanTemplates.find(t => t.id === parseInt(formData.loanTemplateId));
      const loanData = {
        requestedAmount: selectedTemplate?.loanAmount || 0,
        notes: formData.notes,
      };
      await api.post('/officer/loans', loanData, {
        params: {
          customerId: formData.customerId,
          officerId: user.userId,
        },
      });
      setMessage('Loan application created successfully!');
      setFormData({
        customerId: '',
        loanTemplateId: '',
        notes: '',
      });
    } catch (error) {
      setMessage('Error creating loan application: ' + (error.response?.data?.message || error.message));
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Create Loan Application (New Customers Only)
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        This page is for newly registered customers who have no existing loans. For customers with active loans, use "Create Loan for Existing Customer". For customers with closed loans, use "Create Loan for Closed Loan Customers".
      </Alert>
      <Paper sx={{ p: 3, mt: 2 }}>
        {message && (
          <Alert 
            severity={message.includes('Error') || message.includes('WARNING') ? 'warning' : message.includes('success') ? 'success' : 'info'} 
            sx={{ mb: 2 }}
            onClose={() => setMessage('')}
          >
            {message}
          </Alert>
        )}
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Customer"
                required
                value={formData.customerId}
                onChange={(e) => setFormData({ ...formData, customerId: e.target.value })}
                helperText={loadingCustomers ? 'Loading customers...' : filteredCustomers.length > 0 ? `Found ${filteredCustomers.length} customer(s) - Select a newly registered customer (no existing loans)` : "No newly registered customers available"}
              >
                {filteredCustomers.length === 0 ? (
                  <MenuItem disabled>No customers available</MenuItem>
                ) : (
                  filteredCustomers.map((customer) => (
                    <MenuItem key={customer.id} value={customer.id}>
                      {customer.fullName || customer.name} - {customer.nic}
                      {customer.createdAt && ` (Registered: ${new Date(customer.createdAt).toLocaleDateString()})`}
                    </MenuItem>
                  ))
                )}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Loan Amount"
                required
                value={formData.loanTemplateId}
                onChange={(e) => setFormData({ ...formData, loanTemplateId: e.target.value })}
              >
                {loanTemplates.map((template) => (
                  <MenuItem key={template.id} value={template.id}>
                    {template.name} - Rs {template.loanAmount?.toFixed(2)} ({template.numberOfInstallments} installments)
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={4}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <Button type="submit" variant="contained" size="large">
                Submit Loan Application
              </Button>
            </Grid>
          </Grid>
        </form>

        {/* Customer Arrears Summary - Prominent Display */}
        {formData.customerId && customerLoans.length > 0 && (() => {
          const adminClosedLoansWithArrears = customerLoans.filter(
            loan => loan.closedByAdmin && loan.arrearsAmount && loan.arrearsAmount > 0
          );
          const totalArrears = adminClosedLoansWithArrears.reduce(
            (sum, loan) => sum + (parseFloat(loan.arrearsAmount) || 0), 0
          );
          
          if (adminClosedLoansWithArrears.length > 0) {
            return (
              <Box sx={{ mt: 3 }}>
                <Card sx={{ bgcolor: 'error.light', border: '2px solid', borderColor: 'error.main' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ color: 'error.dark', fontWeight: 'bold' }}>
                      ⚠️ Special Status: Customer Has Outstanding Arrears
                    </Typography>
                    <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                        This customer had {adminClosedLoansWithArrears.length} loan(s) closed by admin with outstanding arrears.
                      </Typography>
                      <Typography variant="h6" sx={{ color: 'error.dark', fontWeight: 'bold' }}>
                        Total Outstanding Arrears: Rs {totalArrears.toFixed(2)}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        Please review the loan history below for detailed information before proceeding with the loan application.
                      </Typography>
                    </Alert>
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                        Loans with Arrears:
                      </Typography>
                      {adminClosedLoansWithArrears.map((loan, idx) => (
                        <Typography key={idx} variant="body2" sx={{ ml: 2 }}>
                          • Loan #{loan.id}: Rs {loan.arrearsAmount?.toFixed(2) || '0.00'} 
                          {loan.closedAt && ` (Closed: ${new Date(loan.closedAt).toLocaleDateString()})`}
                        </Typography>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            );
          }
          return null;
        })()}

        {/* Customer Loan History */}
        {formData.customerId && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" gutterBottom>
              Customer Loan History
            </Typography>
            {loadingLoans ? (
              <Typography>Loading loan history...</Typography>
            ) : customerLoans.length === 0 ? (
              <Alert severity="info">
                This customer has no existing loans. This is a fresh loan application.
              </Alert>
            ) : (
              <>
              {customerLoans.some(loan => loan.closedByAdmin && loan.arrearsAmount > 0) && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <strong>Special Status:</strong> This customer had previous loans closed by admin with arrears. 
                  Please review the loan history below for details.
                </Alert>
              )}
              <TableContainer component={Paper} sx={{ mt: 2 }}>
                <Table size="small">
                  <TableHead>
                      <TableRow>
                        <TableCell><strong>Loan ID</strong></TableCell>
                        <TableCell><strong>Status</strong></TableCell>
                        <TableCell><strong>Requested Amount</strong></TableCell>
                        <TableCell><strong>Approved Amount</strong></TableCell>
                        <TableCell><strong>Arrears</strong></TableCell>
                        <TableCell><strong>Created Date</strong></TableCell>
                      </TableRow>
                  </TableHead>
                  <TableBody>
                    {customerLoans.map((loan) => (
                      <TableRow key={loan.id}>
                        <TableCell>{loan.id}</TableCell>
                        <TableCell>
                          <Chip
                            label={loan.status}
                            color={
                              loan.status === 'ACTIVE' ? 'success' :
                              loan.status === 'APPROVED' ? 'info' :
                              loan.status === 'PENDING' ? 'warning' :
                              loan.status === 'REJECTED' ? 'error' : 'default'
                            }
                            size="small"
                          />
                        </TableCell>
                        <TableCell>Rs {loan.requestedAmount?.toFixed(2) || '0.00'}</TableCell>
                        <TableCell>Rs {loan.approvedAmount?.toFixed(2) || 'N/A'}</TableCell>
                        <TableCell>
                          {loan.closedByAdmin && loan.arrearsAmount && loan.arrearsAmount > 0 ? (
                            <Chip 
                              label={`Rs ${loan.arrearsAmount.toFixed(2)}`}
                              color="error"
                              size="small"
                              title="This loan was closed by admin with arrears deducted from capital"
                            />
                          ) : loan.closedByAdmin ? (
                            <Chip 
                              label="Closed by Admin"
                              color="warning"
                              size="small"
                            />
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell>
                          {loan.createdAt ? new Date(loan.createdAt).toLocaleDateString() : 'N/A'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              </>
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default LoanApplication;

